import { SupportedLanguages } from "@/enums/internationalization";
import { LangString } from "@/types/api";
import { MainAlertProps } from "@/types/common/alerts";
import { TOKEN } from "@/types/common/ids";
import { apiFetch } from "@/lib/api";

export const SmartT = (
  key: string,
  t: (val: string, data?: any) => string,
  data?: { [key: string]: any }
) => {
  const translatedValue = t(key.toLowerCase(), data);

  if (translatedValue === key.toLowerCase()) {
    return (
      key +
      (data
        ? ` - ${Object.keys(data)
            .map((d) => data[d])
            .join(",")}`
        : "")
    );
  }

  return translatedValue;
};

export const TranslateHelper = (
  translateFunc: (key: string) => string,
  key: string | undefined
) => {
  let translatedValue = translateFunc((key || "").toLowerCase());
  if (translatedValue !== (key || "").toLowerCase()) {
    return translatedValue;
  }

  return key || "";
};

export const AutoTranslate = async (
  val: LangString,
  lang: SupportedLanguages,
  token: TOKEN | (() => Promise<TOKEN>),
  t: (key: string) => string,
  setAlert: (props: MainAlertProps) => void,
  setIsLoading?: (
    isSaving: boolean,
    message: string,
    description?: string
  ) => void,
  languages?: SupportedLanguages[]
): Promise<LangString> => {
  if (!val[lang]) {
    setAlert({
      status: "error",
      message: t("missingText"),
      secondsToClose: 10,
    });
    return val;
  }

  if (setIsLoading) {
    setIsLoading(true, t("translatingText"));
  }

  const reply = await apiFetch<LangString>("/api/translate", "POST", { body: {
      value: val[lang] || "",
      lang,
      languages,
    } });

  if (setIsLoading) {
    setIsLoading(false, "");
  }

  if (!reply) {
    setAlert({
      status: "error",
      message: t("translationFailed"),
      secondsToClose: 10,
    });
    return val;
  }

  return reply;
};

export const GetTextFromLangString = (
  val: LangString | undefined,
  lang: SupportedLanguages,
  defaultLang: SupportedLanguages = SupportedLanguages.English
): string => {
  if (!val) {
    return "";
  }

  // Find the correct matching language
  if (val[lang]) {
    return val[lang] || "";
  }

  // If the requested language is not found, return the default language
  if (val[defaultLang]) {
    return val[defaultLang] || "";
  }

  //Find the first non-empty value
  for (const key in val) {
    const langKey = key as SupportedLanguages;
    if (val[langKey]) {
      return val[langKey] || "";
    }
  }

  return "";
};

export const GetLangStringValidation = (
  str?: LangString,
  requireAll?: boolean
) => {
  if (!str) {
    return false;
  }

  if (requireAll) {
    return Object.values(SupportedLanguages).every((lang) => {
      if (!str[lang] || (str[lang] ?? "").trim() === "") {
        return false;
      }
      return true;
    });
  }

  return Object.values(str).every((value) => value.trim() !== "");
};
